import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Fade,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBackIosRounded as ArrowBackIosRoundedIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getChatState,
  removeConversation,
  selectConversation,
  toggleNewChat,
} from '../../../../store/V2/ChatSlice';
import {
  getConversationAvatar,
  getConversationName,
  getDetailedLastSeen,
} from '../chatHelpers';

interface ChatHeaderProps {
  onChatCollapse: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ onChatCollapse }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const { user } = useAppSelector(getAuthState);
  const {
    isNewChat,
    conversations,
    selectedConversation,
    conversationTypingData,
  } = useAppSelector(getChatState);

  // State
  const [, forceUpdate] = useState(0);

  const _matchingConversationTypingData = conversationTypingData.find(
    (item) =>
      selectedConversation &&
      user &&
      item.conversationId === selectedConversation._id &&
      item.userId !== user._id
  );

  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate((prev) => prev + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleBackClick = () => {
    dispatch(removeConversation());

    if (isNewChat) {
      dispatch(toggleNewChat());

      if (conversations.length === 1) {
        dispatch(selectConversation(conversations[0]));
      }
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      padding="18px 13px 8px"
    >
      <Box display="flex" alignItems="center">
        {((isNewChat || (selectedConversation && conversations.length > 1)) &&
          !isXsScreen) ||
        isXsScreen ? (
          <IconButton
            size="small"
            onClick={() => {
              if (
                isNewChat ||
                (selectedConversation && conversations.length > 1)
              ) {
                handleBackClick();
              } else {
                onChatCollapse();
              }
            }}
          >
            <ArrowBackIosRoundedIcon />
          </IconButton>
        ) : null}

        {selectedConversation ? (
          <>
            {getConversationAvatar(selectedConversation, user?._id)}
            <Box marginLeft="10px">
              <Typography
                fontSize="16px"
                fontWeight="600"
                lineHeight="20px"
                color={TP_COLORS.gray.dark}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {getConversationName(selectedConversation, user?._id)}
              </Typography>
              {selectedConversation.users.length > 1 ? (
                <Typography fontSize="12px" color="#647488">
                  {_matchingConversationTypingData ? (
                    <Fade in>
                      <span>Typing...</span>
                    </Fade>
                  ) : (
                    <Fade in>
                      <span>
                        {getDetailedLastSeen(
                          selectedConversation,
                          user?._id || ''
                        )}
                      </span>
                    </Fade>
                  )}
                </Typography>
              ) : null}
            </Box>
          </>
        ) : (
          <Typography
            variant="h5"
            // fontSize="26px"
            fontWeight="600"
            marginLeft="7px"
            color={TP_COLORS.gray.dark}
          >
            {isNewChat ? 'New Chat' : 'Chats'}
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="center">
        {!isNewChat && (conversations.length <= 1 || !selectedConversation) ? (
          <IconButton size="small" onClick={() => dispatch(toggleNewChat())}>
            <AddCircleIcon sx={{ color: '#0D71D9' }} />
          </IconButton>
        ) : null}

        {!isXsScreen ? (
          <IconButton
            onClick={onChatCollapse}
            sx={{
              backgroundColor: '#F8FAFC',
              borderRadius: '5px',
              marginLeft: '8px',
            }}
          >
            <img
              style={{ width: '22px', height: '18px' }}
              src={`${process.env.PUBLIC_URL}/images/chat/chat-collapse.png`}
              alt="chat-collapse"
            />
          </IconButton>
        ) : null}
      </Box>
    </Stack>
  );
};

export default ChatHeader;
